import React, { useEffect, useState } from 'react';
import { useAolStore } from '../../context/AolContext';
import { useVesselStore } from '../../context/VesselContext';
import { Period } from '../../enums/PeriodEnum';
import { useUserSettingsStore } from '../../context/UserSettingsContext';
import { RoleTypeEnum } from '../../enums/roleTypeEnum';
import { useAuthStore } from '../../context/AuthContext';
import useCargoTemperatureApi from '../../api/cargo-temp-log/useCargoTempLogApi';
import { LeftWrapper, MainPanel, PageContentWrapper } from './cargoTempLog.styles';
import CargoTempListComponent from '../../components/cargo-temp-log/cargo-temp-date-list/CargoTempDateListComponent';
import CargoTempLogHeaderComponent from '../../components/cargo-temp-log/CargoTempLogHeader';
import useCargoTempLogApi from '../../api/cargo-temp-log/useCargoTempLogApi';
import CargoTempLogReportingComponent from '../../components/cargo-temp-log/cargo-temp-log-reporting/CargoTempLogReporting';
import { useCargoTempLogStore } from '../../context/CargoTempLogContext';
import { CargoTemperatureLogDate } from '../../types/ViewModel/CargoTempLog/CargoTemperatureLogDate';
import { compareArrays, getDistinctValues, getStringValue } from '../../utils/arrayUtils';
import { addDateDays, isDateAfter, isDateBefore, isDateBetween, isDateSame, isDateSameOrBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { formatAolUrlDate, getAolDateFromUrlFormat } from '../../utils/aol/aolDateUtils';
import { DateTimeFormat, formatDateTime } from 'odfjell-date-time-utils/dateTimeFormatter';
import queryParamsUtils from '../../utils/queryParamsUtils';
import { useQueryParamsHandler } from 'odfjell-query-manager';
import { intitializeDateFilter } from 'src/utils/dateFilterUtils';
import { useEventStore } from 'src/context/EventContext';
import { populateCargoOnBoardFromStowage } from 'src/utils/cargo-temp-log/cargoTempLogUtils';
import { useStowageStore } from 'src/context/StowageContext';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import useCheckStowageIntegration from 'src/app/useCheckStowageIntegration';
import useAppFeature from 'src/app/useAppFeature';

const CargoTempLogComponent: React.FC = () => {
    const { getCargoTemperatureInputFields, getCargoTemperatureConfigs } = useCargoTemperatureApi();
    const { getCargoTempLogDates } = useCargoTempLogApi();

    const { aolFilter, setAolFilter, setDefaultAolFilter, defaultAolFilter, period, setPeriod, aolListSortAsc } = useAolStore();
    const { cargoTempLogDates, cargoTemperatureConfigs } = useCargoTempLogStore();
    const { userSettings, userSettingsLoaded } = useUserSettingsStore();
    const { selectedVessel } = useVesselStore();
    const { role } = useAuthStore();
    const { cargoOperationsEvents } = useEventStore();
    const { getAppFeatureFromDateForVessel } = useAppFeature();
    const { loadedMasterStowagePlan } = useStowageStore();
    const { checkLoadedStowageIntegration } = useCheckStowageIntegration();

    const { setQueryParamCargoTempLog } = queryParamsUtils();
    const { getQueryParamValueFromUrl } = useQueryParamsHandler();

    const [populatedCargoTempLogDates, setPopulatedCargoTempLogDates] = useState<CargoTemperatureLogDate[]>([]);
    const [initialized, setInitialized] = useState<boolean>(false);

    // ============= USE EFFECTS ================

    useEffect(() => {
        return () => {
            resetContext();
        };
    }, []);

    // Initialize the component when vessel or user settings change
    useEffect(() => {
        if (!userSettingsLoaded || !selectedVessel) return;

        if (!initialized) {
            intitializeDateFilter(userSettings, aolFilter, setAolFilter, setDefaultAolFilter, setPeriod, role);
            setInitialized(true);
            return;
        }

        if (period === Period.Custom) {
            setPeriod(userSettings?.aolPeriod ?? role?.roleType === RoleTypeEnum.ShipUser ? Period.TwoWeeks : Period.FourWeeks);
        }
    }, [selectedVessel, userSettingsLoaded, initialized]);

    // Handle filter updates when vessel changes or URL parameters change
    useEffect(() => {
        if (!selectedVessel || !initialized || !aolFilter) return;

        const aolDateStr = getQueryParamValueFromUrl('aolDate');
        const ls_userChangedVessel = sessionStorage.getItem('userChangedVessel');

        let fromDateStr = formatAolUrlDate(aolFilter.fromDate);
        let toDateStr = formatAolUrlDate(aolFilter.toDate);
        let hasDefault = false;
        let hasUpdate = false;

        if (!cargoTempLogDates || cargoTempLogDates.length === 0 || ls_userChangedVessel === 'true') {
            // if aolDateStr exists, but user didnt select a vessel change, assume this is triggered by
            // opening a link directly to an aolDate, set filter range accordingly
            if (aolDateStr && ls_userChangedVessel !== 'true') {
                const aolDateFromQuery = getAolDateFromUrlFormat(aolDateStr);
                const filterFrom = aolFilter.fromDate;
                const maxFromDate = defaultAolFilter.fromDate;
                const maxToDate = defaultAolFilter.toDate;

                if (isDateBefore(aolDateFromQuery, filterFrom, 'day') || isDateBefore(aolDateFromQuery, maxFromDate, 'day')) {
                    fromDateStr = formatAolUrlDate(aolDateFromQuery);
                    toDateStr = formatAolUrlDate(addDateDays(aolDateFromQuery, period === Period.TwoWeeks ? 14 : 28));
                    hasUpdate = true;
                } else if (
                    isDateAfter(aolDateFromQuery, filterFrom, 'day') &&
                    (isDateBetween(aolDateFromQuery, maxFromDate, maxToDate, 'day') || isDateAfter(aolDateFromQuery, maxToDate, 'day'))
                ) {
                    fromDateStr = formatAolUrlDate(maxFromDate);
                    toDateStr = formatAolUrlDate(maxToDate);
                    hasDefault = true;
                    hasUpdate = true;
                }
            }
            // user switched vessel, set filter to default
            if (ls_userChangedVessel === 'true') {
                sessionStorage.setItem('userChangedVessel', 'false');
                hasDefault = true;
                hasUpdate = true;
            }
        }

        if (hasUpdate) {
            setAolFilter({
                fromDate: getAolDateFromUrlFormat(fromDateStr),
                toDate: getAolDateFromUrlFormat(toDateStr),
                isDefault: hasDefault,
            });
        }
    }, [selectedVessel, initialized, cargoTempLogDates]);

    // Load cargo temp log dates when vessel or filter changes (but not during initialization)
    useEffect(() => {
        if (!selectedVessel || !aolFilter || !initialized) {
            return;
        }

        const stowageIntegrationFromDate = getAppFeatureFromDateForVessel({
            featureEnum: AppFeatureEnum.StowageIntegration,
            imoNumber: selectedVessel.imoNumber,
        });

        const fromDateStr = formatAolUrlDate(aolFilter.fromDate);
        const toDateStr = formatAolUrlDate(aolFilter.toDate);

        getCargoTempLogDates(selectedVessel.imoNumber, fromDateStr, toDateStr, formatAolUrlDate(stowageIntegrationFromDate), false);
    }, [
        selectedVessel?.imoNumber,
        aolFilter ? formatAolUrlDate(aolFilter.fromDate) : null,
        aolFilter ? formatAolUrlDate(aolFilter.toDate) : null,
        initialized,
    ]);

    useEffect(() => {
        if (!populatedCargoTempLogDates || populatedCargoTempLogDates.length === 0 || !selectedVessel) return;

        const ss_userChangedFilter = sessionStorage.getItem('userChangedFilter');

        const aolDateStr = getQueryParamValueFromUrl('aolDate');

        // if no aolDate is selected or user changes filter, set selected aolDate to latest in range
        if (!aolDateStr || ss_userChangedFilter === 'true') {
            const _sortedCargoTempLogDates = populatedCargoTempLogDates.sort((a, b) =>
                aolListSortAsc ? (isDateBefore(a.logDate, b.logDate) ? 1 : -1) : isDateBefore(a.logDate, b.logDate) ? -1 : 1
            );

            const firstDateInList = formatDateTime(_sortedCargoTempLogDates[0].logDate, DateTimeFormat.urlFormats.dateOnly).toString();

            if (ss_userChangedFilter === 'true') sessionStorage.setItem('userChangedFilter', 'false');

            setQueryParamCargoTempLog(firstDateInList);
        }
    }, [populatedCargoTempLogDates]);

    useEffect(() => {
        if (!cargoTempLogDates || cargoTempLogDates.length === 0 || !selectedVessel) return;

        if (aolFilter && selectedVessel) {
            const _populatedCargoTempLogDates: CargoTemperatureLogDate[] = [];

            const stowageIntegrationFromDate = getAppFeatureFromDateForVessel({
                featureEnum: AppFeatureEnum.StowageIntegration,
                imoNumber: selectedVessel.imoNumber,
            });

            let trailingDate = aolFilter.fromDate;
            trailingDate.setHours(0, 0, 0, 0);
            let toDate = aolFilter.toDate;
            if (isDateAfter(toDate, new Date())) toDate = new Date();

            while (isDateSameOrBefore(trailingDate, toDate, 'day')) {
                const existingCargoTempLogDate = cargoTempLogDates.find((d) => isDateSame(d.logDate, trailingDate, 'day'));
                if (existingCargoTempLogDate) {
                    _populatedCargoTempLogDates.push(existingCargoTempLogDate);
                } else {
                    const newCargoTempLogDate = {
                        logDateId: 0,
                        imoNumber: selectedVessel.imoNumber,
                        logDate: trailingDate,
                        isComplete: false,
                        lastUpdatedUtc: null,
                        lastUpdatedBy: null,
                        cargoInTankList: [],
                        isValidated: false,
                    };
                    _populatedCargoTempLogDates.push(newCargoTempLogDate);
                }

                trailingDate = addDateDays(trailingDate, 1);
            }

            // Check if stowage integration is enabled and if we have the required data
            const isStowageIntegrationEnabled = !checkLoadedStowageIntegration(selectedVessel.imoNumber, loadedMasterStowagePlan);

            if (isStowageIntegrationEnabled && cargoOperationsEvents && cargoOperationsEvents.length > 0 && loadedMasterStowagePlan) {
                populateCargoOnBoardFromStowage(
                    _populatedCargoTempLogDates,
                    loadedMasterStowagePlan,
                    stowageIntegrationFromDate,
                    cargoOperationsEvents,
                    selectedVessel
                );
            }

            let cargoNumbers = [..._populatedCargoTempLogDates]?.flatMap((a) => a.cargoInTankList?.flatMap((b) => b.cargoNumber as string));
            cargoNumbers = getDistinctValues(cargoNumbers).filter((v) => v !== '');
            if (cargoNumbers && cargoNumbers.length > 0) {
                const loadedConfigsCargoNumbers = cargoTemperatureConfigs?.map((c) => c.cargoNumber) ?? [];
                const cargoNumbersStr = getStringValue(cargoNumbers, ',');
                if (!compareArrays(cargoNumbers, loadedConfigsCargoNumbers)) {
                    getCargoTemperatureConfigs(selectedVessel?.imoNumber, cargoNumbersStr);
                }
            }

            const _sortedCargoTempLogDates = _populatedCargoTempLogDates.sort((a, b) =>
                aolListSortAsc ? (isDateBefore(a.logDate, b.logDate) ? 1 : -1) : isDateBefore(a.logDate, b.logDate) ? -1 : 1
            );

            setPopulatedCargoTempLogDates([..._sortedCargoTempLogDates]);
        }
    }, [cargoTempLogDates, aolFilter, loadedMasterStowagePlan, selectedVessel, cargoOperationsEvents]);

    useEffect(() => {
        if (aolFilter) {
            setPopulatedCargoTempLogDates(
                populatedCargoTempLogDates.sort((a, b) =>
                    aolListSortAsc ? (isDateBefore(a.logDate, b.logDate) ? 1 : -1) : isDateBefore(a.logDate, b.logDate) ? -1 : 1
                )
            );
        }
    }, [aolListSortAsc]);

    // Load cargo temperature input fields when vessel changes
    useEffect(() => {
        if (!selectedVessel) return;
        getCargoTemperatureInputFields(selectedVessel.imoNumber);
    }, [selectedVessel]);

    const resetContext = () => {
        setPopulatedCargoTempLogDates(null);
    };

    return (
        <PageContentWrapper>
            <LeftWrapper>
                <CargoTempListComponent populatedCargoTempLogDates={populatedCargoTempLogDates} cargoTemperatureConfigs={cargoTemperatureConfigs} />
            </LeftWrapper>
            <MainPanel>
                {selectedVessel && (
                    <>
                        <CargoTempLogHeaderComponent
                            populatedCargoTempLogDates={populatedCargoTempLogDates}
                            cargoTemperatureConfigs={cargoTemperatureConfigs}
                        />
                        <CargoTempLogReportingComponent
                            populatedCargoTempLogDates={populatedCargoTempLogDates}
                            imoNumber={selectedVessel.imoNumber}
                        />
                    </>
                )}
            </MainPanel>
        </PageContentWrapper>
    );
};

export default CargoTempLogComponent;
